export function normalizeOrderNo(value) {
  return String(value ?? '').trim().toUpperCase();
}

export function isIssueWarning(warning) {
  const severity = String(warning?.severity ?? '').toLowerCase();
  return severity === 'warning' || severity === 'error' || severity === 'critical';
}

export function formatDateTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
}

export function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit'
  }).format(date);
}

export function isCustomLogoServiceLine(item) {
  return String(item?.styleCode ?? '').trim().toLowerCase() === 'custom logo';
}

export function getFulfillmentDisplay(item, lifecycle) {
  if (isCustomLogoServiceLine(item)) return 'SERVICE';
  return lifecycle?.primaryFulfillmentPath || '—';
}

export function joinOrDash(values) {
  const items = Array.isArray(values)
    ? values.filter(Boolean)
    : String(values ?? '')
        .split(',')
        .map((x) => x.trim())
        .filter(Boolean);
  return items.length ? items.join(', ') : '—';
}

const ORDER_PREFIX_LABELS = Object.freeze({
  AF: 'Amazon Farjazz',
  AP: 'Amazon Pulse Uniform',
  AR: 'Amazon Rizno',
  AJ: 'Amazon Jeffa',
  WP: 'Walmart Pulse Uniform',
  WM: 'Walmart Canada',
  WJ: 'Walmart Jeffa',
  WF: 'Walmart Farjazz',
  TP: 'TikTok Pulse Uniform',
  TJ: 'TikTok Jeffa',
  EP: 'Ebay Pulse Uniform',
  PU: 'Pulse Uniform',
  JLC: 'Just Labcoats',
  RC: 'Reliable Chef',
  HF: 'HeedFit',
  GC: 'GlyCare'
});

export function getStoreLabel(context) {
  const orderNo = String(context?.order?.orderNo ?? '').trim().toUpperCase();
  const match = /^([A-Z]+)-/.exec(orderNo);
  if (!match) return null;

  return ORDER_PREFIX_LABELS[match[1]] ?? null;
}

export function getShipmentEvidence(context) {
  const shipment = (context?.tracking ?? []).find(
    (x) => x.trackingContextType === 'CUSTOMER_SHIPMENT'
  );
  if (!shipment) return { label: 'No customer shipment record', trackingNo: null };
  return {
    label: 'Customer shipment recorded',
    trackingNo: shipment.trackingNo ?? null
  };
}

export function getItemLineOutcomes(items) {
  const source = Array.isArray(items) ? items : [];

  const groups = new Map();
  for (const item of source) {
    const rawStatus = String(item?.itemStatus ?? 'Unknown').trim() || 'Unknown';
    const key = rawStatus.toLowerCase();
    const existing = groups.get(key) ?? { status: rawStatus, count: 0 };
    existing.count += 1;
    groups.set(key, existing);
  }

  const order = [
    'shipped',
    'dropship',
    'drop ship',
    'inprocess',
    'in process',
    'pending',
    'canceled',
    'cancelled'
  ];

  const rank = (key) => {
    const index = order.indexOf(key);
    return index === -1 ? 999 : index;
  };

  const outcomes = [...groups.entries()]
    .sort(([a], [b]) => rank(a) - rank(b) || a.localeCompare(b))
    .map(([, value]) => ({
      count: value.count,
      status: value.status,
      label: friendlyItemStatus(value.status)
    }));

  if (!outcomes.length) {
    return {
      value: 'No item lines',
      detail: 'No item-line status is present in this context'
    };
  }

  return {
    value: outcomes
      .map((x) => `${x.count} ${x.label}`)
      .join(' · '),
    detail: `${source.length} item line${source.length === 1 ? '' : 's'} total`
  };
}

export function resolveCustomizationItemStatus(customizationItem, items) {
  const itemNo = customizationItem?.itemNo;
  if (itemNo == null) return null;

  const orderItem = (Array.isArray(items) ? items : []).find(
    (item) => item?.itemNo === itemNo
  );

  return orderItem?.itemStatus ?? null;
}

export function friendlyItemStatus(status) {
  const normalized = String(status ?? '').trim().toLowerCase();

  if (normalized === 'inprocess' || normalized === 'in process') return 'in process';
  if (normalized === 'dropship' || normalized === 'drop ship') return 'drop ship';
  if (normalized === 'canceled' || normalized === 'cancelled') return 'canceled';
  if (normalized === 'shipped') return 'shipped';
  if (normalized === 'pending') return 'pending';
  if (!normalized || normalized === 'unknown') return 'unknown';

  return String(status).trim().toLowerCase();
}

export function expectedShipPresentation(order, signals) {
  if (!order?.expectedShipDate) {
    return {
      value: 'Not recorded',
      detail: 'No order-level expected ship date'
    };
  }

  if (signals?.isPastExpectedShipDate) {
    return {
      value: formatDate(order.expectedShipDate),
      detail: `${signals.businessDaysPastExpectedShipDate ?? 0} business day(s) past`
    };
  }

  return {
    value: formatDate(order.expectedShipDate),
    detail: 'Not marked past expected date'
  };
}

export function humanizeCopilotText(value) {
  let text = String(value ?? '');

  // Convert ISO timestamps in model-written text into the same human-readable
  // format used elsewhere in the CSR workspace. This is presentation-only.
  text = text.replace(
    /["']?(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})?)["']?/g,
    (_, iso) => formatDateTime(iso)
  );

  const replacements = [
    [/\bhasEmbroideryOperationsRecord\b/g, 'embroidery operations record'],
    [/\bhasDigitizingRecord\b/g, 'digitizing record'],
    [/\bdigitizingCreateDate\b/g, 'digitizing created'],
    [/\bdigitizingUploadDate\b/g, 'digitizing uploaded'],
    [/\bdigitizingProcessDate\b/g, 'digitizing processed'],
    [/\bdigitizingStatus\b/g, 'digitizing status'],
    [/\bembroideryStatus\b/g, 'embroidery status'],
    [/\breceivedDate\b/g, 'received'],
    [/\bprocessDate\b/g, 'processed'],
    [/\bclosedDate\b/g, 'closed'],
    [/\bexpectedShipDate\b/g, 'expected ship date'],
    [/\bshipDate\b/g, 'ship date'],
    [/\borderDate\b/g, 'order date'],
    [/\bisHold\b/g, 'hold flag'],
    [/\bhasStockLogo\b/g, 'stock logo'],
    [/\bstockLogo1\b/g, 'stock logo 1'],
    [/\bstockLogo2\b/g, 'stock logo 2'],
    [/\bhasEmbroidery\b/g, 'embroidery'],
    [/\bhasAnyCustomization\b/g, 'customization'],
    [/\bhasMTO\b/g, 'Made To Order'],
    [/\bhasMTOOptions\b/g, 'Made To Order options'],
    [/\bprimaryFulfillmentPath\b/g, 'fulfillment path'],
    [/\bitemStatus\b/g, 'item status'],
    [/\borderStatus\b/g, 'order status'],
    [/\bworkOrderStatus\b/g, 'work-order status']
  ];

  for (const [pattern, replacement] of replacements) {
    text = text.replace(pattern, replacement);
  }

  text = text
    .replace(/\bhold flag\s*=\s*false\b/gi, 'hold flag = No')
    .replace(/\bhold flag\s*=\s*true\b/gi, 'hold flag = Yes')
    .replace(/\bdigitizing record\s*=\s*true\b/gi, 'digitizing record is present')
    .replace(/\bdigitizing record\s*=\s*false\b/gi, 'digitizing record is not present')
    .replace(/\bembroidery operations record\s*=\s*true\b/gi, 'embroidery operations record is present')
    .replace(/\bembroidery operations record\s*=\s*false\b/gi, 'embroidery operations record is not present')
    .replace(/\bembroidery\s*=\s*true\b/gi, 'embroidery is recorded')
    .replace(/\bembroidery\s*=\s*false\b/gi, 'embroidery is not recorded')
    .replace(/\bcustomization\s*=\s*true\b/gi, 'customization is present')
    .replace(/\bcustomization\s*=\s*false\b/gi, 'customization is not present')
    .replace(/\bMade To Order\s*=\s*true\b/gi, 'Made To Order is recorded')
    .replace(/\bMade To Order\s*=\s*false\b/gi, 'Made To Order is not recorded');

  return text;
}

export function isQuestionEchoSituation(situation, question) {
  const situationText = String(situation ?? '').trim().toLowerCase();
  const questionText = String(question ?? '').trim().toLowerCase();

  if (!situationText) return true;
  if (situationText.startsWith('csr asked')) return true;
  if (situationText.startsWith('customer service asked')) return true;

  const compactSituation = situationText.replace(/[?.!]/g, '').replace(/\s+/g, ' ');
  const compactQuestion = questionText.replace(/[?.!]/g, '').replace(/\s+/g, ' ');

  return Boolean(compactQuestion) && compactSituation === compactQuestion;
}

export function getUnifiedCopilotSuggestions(context) {
  if (!context) return [];

  const signals = context.operationalSignals ?? {};
  const items = context.items ?? [];
  const suggestions = ['Brief me on this order'];

  if (
    signals.isPastExpectedShipDate === true
    || Number(signals.businessDaysLateWhenShipped ?? 0) > 0
  ) {
    suggestions.push('Why is this order delayed?');
  }

  if (signals.hasEmbroidery === true || items.some((item) => item.hasEmbroidery === true)) {
    suggestions.push('What stage is the embroidery/logo in?');
  }

  if (items.some((item) => item.hasMTOOptions === true)) {
    suggestions.push('What Made To Order options are on this order?');
  }

  if ((context.returnSummaries ?? []).length > 0) {
    suggestions.push('Is there any return activity on this order?');
  }

  if ((context.shopifyEvents ?? []).length > 0) {
    suggestions.push('What happened on Shopify?');
  }

  // Shipping address remains a useful secure deterministic lookup because
  // raw address PII is deliberately not duplicated on the Overview.
  suggestions.push('What is the shipping address?');

  if (suggestions.length < 6) {
    suggestions.push('What is happening with this order?');
  }

  return suggestions.slice(0, 6);
}

const OPERATIONAL_BLOCKER_LABELS = Object.freeze({
  PURCHASE_ORDER_FULFILLMENT: 'Purchase-order fulfillment',
  INVENTORY_PICKING: 'Inventory committed, not picked',
  INVENTORY_NOT_COMMITTED: 'Inventory not committed',
  ORDER_HOLD_REASON_UNKNOWN: 'Order hold · reason unknown',
  PICKED_NOT_SHIPPED_UNKNOWN: 'Picked, not shipped · downstream unknown',
  FULFILLMENT_PATH_NOT_ESTABLISHED: 'Fulfillment path not established',
  BLOCKER_UNKNOWN: 'Blocker not established'
});

export function getOperationalBlockerLabel(code) {
  const key = String(code ?? '').trim().toUpperCase();
  return OPERATIONAL_BLOCKER_LABELS[key] ?? (key || 'Blocker not established');
}

export function getOperationalBlockerTone(code) {
  const key = String(code ?? '').trim().toUpperCase();
  if (key === 'ORDER_HOLD_REASON_UNKNOWN') return 'warning';
  if (key === 'PURCHASE_ORDER_FULFILLMENT') return 'warning';
  if (key === 'INVENTORY_PICKING') return 'warning';
  if (key === 'INVENTORY_NOT_COMMITTED') return 'warning';
  if (key === 'PICKED_NOT_SHIPPED_UNKNOWN') return 'neutral';
  if (key === 'FULFILLMENT_PATH_NOT_ESTABLISHED') return 'warning';
  return 'neutral';
}
